import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';
import {
  EvidenceBadge, FtcDisclosure, InteractionBanner, StrengthBadge,
} from '../supplements/Disclosures';

// The bridge (R4): beneath an observation on outcome X, the supplement people
// commonly watch X against. The sentence is a watching habit, not an effect —
// "people keeping an eye on resting heart rate often look at omega-3" — and
// the row carries the same honesty the quiz shows: strength, evidence grade,
// the safety gate's note. Add puts it in the stack dated today, so the
// since-you-started read begins counting at once.
//
// The backend decides whether a card gets one (never under an improvement,
// never under a stack card, never something already in the stack); this
// component only renders what it was handed.

const todayIso = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const BridgeSuggestion = ({ s, compact = false }) => {
  const [added, setAdded] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!s) return null;

  const add = async () => {
    setBusy(true);
    try {
      await apiClient.post('/api/regimen', {
        kind: 'supplement', catalog_slug: s.slug, name: s.name, started_at: todayIso(),
      });
      setAdded(true);
      toast.success(`${s.name} added — we'll read what moves from today.`);
    } catch {
      toast.error('Could not add that just now.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-dashed border-line bg-canvas p-3 sm:p-4">
      <p className="text-sm text-fg leading-relaxed">{s.text}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <StrengthBadge strength={s.strength} />
        <EvidenceBadge grade={s.evidence_grade} />
      </div>
      {!compact && s.rationale && (
        <p className="mt-2 text-sm text-fg-2 leading-relaxed">{s.rationale}</p>
      )}
      <InteractionBanner reasons={s.gate?.reasons} />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {added ? (
          <Link
            to="/supplements"
            className="inline-flex items-center rounded-full border border-good-strong/50 px-3 py-1.5 text-sm font-medium text-good-strong dark:text-good"
          >
            In your stack — the read starts today →
          </Link>
        ) : (
          <button
            type="button"
            onClick={add}
            disabled={busy}
            className="inline-flex items-center rounded-full bg-cta px-3 py-1.5 text-sm font-medium text-cta-fg hover:brightness-[1.06] disabled:opacity-50"
          >
            Add to stack
          </button>
        )}
        {s.affiliate_url && (
          <a
            href={s.affiliate_url}
            target="_blank"
            rel="sponsored noopener noreferrer"
            className="text-sm font-medium text-fg-2"
          >
            Where to get it ↗
          </a>
        )}
      </div>
      <FtcDisclosure hasAffiliate={!!s.affiliate_url} />
    </div>
  );
};

export default BridgeSuggestion;
